import React from 'react';
import ReactDOM from 'react-dom/client';
import FoliumMap from '../components/FoliumMap';
import { authorization, fetchOrUpdateIdToken } from '../auth/Authorization';
import { localConfig } from '../config/localConfig';
import { tabStateCache } from './tabStateCache';
import { attachSession, isValidSessionError, handleSessionExpired } from './sessionManager';

/**
 * Renders untrusted server HTML inside a sandboxed iframe instead of injecting
 * it into the parent document via innerHTML.
 *
 * Security rationale (XSS remediation):
 * ----------------------------------------------------------------------------
 * The previous implementation assigned `data.Response` (server-controlled HTML)
 * directly to `content.innerHTML`. Any <script> embedded in that HTML ran with
 * full access to the parent application's DOM, cookies, and origin - a classic
 * XSS vector.
 *
 * This helper renders the HTML inside a sandboxed iframe instead. Embedded
 * <script> tags still execute (preserving tab-switching, copy-to-clipboard,
 * map rendering, etc.), but they run in an isolated browsing context. A
 * malicious script is therefore contained to the iframe and cannot reach the
 * parent application's DOM, storage, or cookies. This is the same sandboxing
 * pattern already used for Commercial CIBIL credit reports, Folium maps, and
 * the HtmlContent component elsewhere in this codebase.
 *
 * NOTE: `allow-same-origin` is intentionally included so the embedded scripts
 * can access the same-origin static assets they rely on. If the `html` ever
 * contains untrusted third-party content, remove `allow-same-origin` to further
 * harden the sandbox.
 *
 * @param {HTMLElement} container - The DOM element to render content into.
 * @param {string} html - The raw (untrusted) HTML string to render.
 * @param {string} [height='600px'] - Fallback CSS height for the iframe. The
 *   iframe is auto-sized to its content height on load so the page scrolls
 *   naturally (matching the pre-sandbox behavior) instead of showing a nested
 *   scrollbar inside the iframe.
 */
const renderContentInSandbox = (container, html, height = '600px') => {
    // Clear existing content
    container.innerHTML = '';

    // Wrap partial HTML (not a full document) in a complete HTML document so
    // the browser parses and executes it exactly like a standalone page.
    const content = html || '';
    const srcDoc = /<!DOCTYPE html>/i.test(content) || /<html[\s>]/i.test(content)
        ? content
        : `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
  </head>
  <body>
    ${content}
  </body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.srcDoc = srcDoc;
    iframe.title = 'Embedded content';
    iframe.setAttribute(
        'sandbox',
        'allow-scripts allow-same-origin allow-popups allow-forms allow-modals'
    );
    iframe.style.width = '100%';
    iframe.style.height = height;
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';

    // Auto-size the iframe to the height of its content so the page scrolls
    // naturally (matching the pre-sandbox behavior where content flowed in the
    // parent document) instead of showing a nested scrollbar inside the iframe.
    // The iframe is sandboxed with allow-same-origin, so we can read its
    // contentDocument to measure the body height.
    iframe.onload = () => {
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc) return;
            const body = doc.body;
            const htmlEl = doc.documentElement;
            if (!body) return;
            // Use the larger of body.scrollHeight / documentElement.scrollHeight
            // to account for margins and content that overflows the body.
            const contentHeight = Math.max(
                body.scrollHeight,
                body.offsetHeight,
                htmlEl.scrollHeight,
                htmlEl.offsetHeight
            );
            if (contentHeight > 0) {
                iframe.style.height = `${contentHeight}px`;
            }
        } catch (e) {
            // Cross-origin access is blocked; keep the fallback height.
            console.warn('[renderContentInSandbox] Could not auto-size iframe:', e);
        }
    };

    container.appendChild(iframe);
};

// Function to render FoliumMap component dynamically
const renderFoliumMap = (containerId, htmlContent, options = {}) => {
    const container = document.getElementById(containerId);
    if (!container) {
        throw new Error(`Container ${containerId} not found`);
    }
    
    // Clear existing content
    container.innerHTML = '';
    
    // Create React root and render FoliumMap component
    const root = ReactDOM.createRoot(container);
    root.render(
        React.createElement(FoliumMap, {
            htmlContent: htmlContent,
            height: options.height || '400px',
            width: options.width || '100%'
        })
    );
    
    return root; // Return root for potential cleanup
};

const processAsyncDocument = async function(leadId, docType, options = {}) {
    // Validate required parameters
    if (!leadId || !docType) {
        throw new Error('leadId and docType are required parameters');
    }
    if (!options.buttonId || !options.contentId || !options.userId || !options.source) {
        throw new Error('buttonId, contentId, userId, and source are required in options');
    }
    
    // Get DOM elements
    const btn = document.getElementById(options.buttonId);
    const content = document.getElementById(options.contentId);
    
    if (!btn || !content) {
        throw new Error(`Button (${options.buttonId}) or content container (${options.contentId}) not found`);
    }
    
    // Store original button content for restoration
    const originalContent = btn.innerHTML;
    const docName = options.docName || docType.replace('_', ' ').toUpperCase();
    
    // Special handling for image insights with polling
    if (docType === 'image_insights') {
        return await handleImageInsightsPolling(leadId, options);
    }
    
    // Set loading state for regular documents
    // Static loading spinner. The @keyframes camCopilotSpin is defined in
    // src/index.css (moved out of this file to avoid template-literal false
    // positives). No user-controlled data is interpolated here.
    btn.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><div style="width: 16px; height: 16px; border: 2px solid #ffffff40; border-top: 2px solid #ffffff; border-radius: 50%; animation: camCopilotSpin 1s linear infinite;"></div><span>Loading...</span></div>';
    btn.disabled = true;
    btn.style.cursor = 'not-allowed';
    btn.style.opacity = '0.8';
    
    try {
        // Get authentication token for API requests
        const app_name = localConfig.appName;
        const validatedIdToken = await fetchOrUpdateIdToken(
            sessionStorage.getItem("idToken"),
            sessionStorage.getItem("exp")
        );
        const authResult = await authorization(validatedIdToken.idToken, app_name);

        // Check if user has access to the application
        if (!authResult.hasAccess) {
            throw new Error('Access Denied: You do not have permission to access this application.');
        }
        
        // Use local configuration for API URL
        const apiUrl = `${localConfig.apiBaseUrl}/process_document`;
        
        
        // Make API request
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': authResult.accessToken,
            },
            body: JSON.stringify(attachSession({
                query: leadId,
                user: options.userId,
                source: options.source,
                doc_type: docType
            }))
        });
        
        // Check if response is ok
        if (!response.ok) {
            // Force-logout if the session was invalidated by a newer sign-in.
            if (await isValidSessionError(response)) {
                handleSessionExpired();
                return;
            }
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        // Parse response
        const data = await response.json();
        
        if (!data.Response) {
            throw new Error('Invalid response format from server');
        }
        
        // Check if this is map content and handle accordingly
        if (docType === 'map_insights' || docType === 'industry_insights') {
            // Use blob URL approach to handle large coordinate data
            
            try {
                // Render the map HTML inside a sandboxed iframe. The content is
                // server-controlled, so it must not be injected into the parent
                // document via innerHTML (XSS). The sandboxed iframe isolates any
                // embedded scripts from the parent application's DOM/origin.
                renderContentInSandbox(content, data.Response, options.height || '600px');
            } catch (error) {
                console.error('Error creating blob URL for map:', error);
                // Fallback to regular content rendering
                content.innerHTML = `
                    <div style="
                        color: #dc2626; 
                        text-align: center; 
                        margin-top: 15px; 
                        padding: 15px; 
                        background-color: #fee2e2; 
                        border: 1px solid #fecaca;
                        border-radius: 8px;
                        font-weight: 500;
                    ">
                        <i class="fas fa-exclamation-triangle" style="margin-right: 8px;"></i>
                        Error rendering map. The map data may be too large.
                    </div>
                `;
            }
        } else {
            // Render the server-controlled HTML inside a sandboxed iframe.
            // Injecting it directly via innerHTML would be an XSS vector.
            renderContentInSandbox(content, data.Response);
        }
        
        // Cache the updated content for state preservation
        try {
            // Generate cache key using leadId and docType
            const cacheKey = tabStateCache.generateKey(leadId, docType);
            
            // Store the cache key in the content container as a data attribute
            // so components can find the right cache entry
            content.setAttribute('data-cache-key', cacheKey);
            content.setAttribute('data-lead-id', leadId);
            content.setAttribute('data-doc-type', docType);
            
            // Cache the raw server HTML (not the sandboxed iframe's innerHTML).
            // On restore, useCachedContent -> HtmlContent re-renders this string
            // inside a sandboxed iframe, so caching the raw string keeps the
            // restore path consistent and avoids nesting iframes.
            const contentToCache = data.Response;
            
            tabStateCache.store(cacheKey, contentToCache, {
                docType: docType,
                timestamp: Date.now(),
                leadId: leadId,
                userId: options.userId
            });
        } catch (cacheError) {
            console.warn('[processAsyncDocument] Failed to cache content:', cacheError);
            // Don't fail the entire operation if caching fails
        }
        
        btn.style.display = 'none';
        
        // Return the content for any additional processing
        return data.Response;
        
    } catch (error) {
        console.error('Error loading %s:', docName, error);
        
        // Show error message in content area
        content.innerHTML = `
            <div style="
                color: #dc2626; 
                text-align: center; 
                margin-top: 15px; 
                padding: 15px; 
                background-color: #fee2e2; 
                border: 1px solid #fecaca;
                border-radius: 8px;
                font-weight: 500;
            ">
                <i class="fas fa-exclamation-triangle" style="margin-right: 8px;"></i>
                Error loading ${docName}. Please try again.
                <small style="color: #991b1b; margin-top: 5px; display: block;">
                    ${error.message || 'Network error occurred'}
                </small>
            </div>
        `;
        
        // Restore button for retry
        btn.innerHTML = '<i class="fas fa-redo"></i> Retry';
        btn.disabled = false;
        btn.style.cursor = 'pointer';
        btn.style.opacity = '1';
        
        // Re-throw error for any additional error handling
        throw error;
    }
};

// Internal polling handler for Image Insights (not exposed globally)
const handleImageInsightsPolling = async function(leadId, options) {
    const btn = document.getElementById(options.buttonId);
    const content = document.getElementById(options.contentId);
    const docName = options.docName || 'Image Insights';
    
    // Configuration (hardcoded within function)
    let pollingInterval = 3000; // 3 seconds (matches backend)
    let maxTimeout = 60000;    // 1 minute
    let startTime = Date.now();
    
    const poll = async () => {
        try {
            // Get authentication token for API requests
            const app_name = localConfig.appName;
            const validatedIdToken = await fetchOrUpdateIdToken(
                sessionStorage.getItem("idToken"),
                sessionStorage.getItem("exp")
            );
            const authResult = await authorization(validatedIdToken.idToken, app_name);

            // Check if user has access to the application
            if (!authResult.hasAccess) {
                throw new Error('Access Denied: You do not have permission to access this application.');
            }
            
            // Use local configuration for API URL
            const apiUrl = `${localConfig.apiBaseUrl}/process_document`;
            
            // Make API request with additional parameters (including analysis start time for subsequent requests)
            const requestBody = {
                query: leadId,
                user: options.userId,
                source: options.source,
                doc_type: 'image_insights',
                pdt: options.pdt,        // Passed from CTA button
                sub_pdt: options.sub_pdt // Passed from CTA button
            };
            
            // Add analysis start time if we have it from previous response
            if (options.analysisStartTime) {
                requestBody.analysis_start_time = options.analysisStartTime;
            }
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': authResult.accessToken,
                },
                body: JSON.stringify(attachSession(requestBody))
            });
            
            // Check if response is ok
            if (!response.ok) {
                if (await isValidSessionError(response)) {
                    handleSessionExpired();
                    return;
                }
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }
            
            // Parse response
            const data = await response.json();
            
            if (!data.Response) {
                throw new Error('Invalid response format from server');
            }
            
            // Parse status from response
            const parser = new DOMParser();
            const doc = parser.parseFromString(data.Response, 'text/html');
            const statusDiv = doc.querySelector('[data-status]');
            const status = statusDiv?.getAttribute('data-status') || 'UNKNOWN';
            const analysisStartTime = statusDiv?.getAttribute('data-analysis-start-time');
            
            // Update polling configuration from server response
            const serverPollingInterval = parseInt(statusDiv?.getAttribute('data-polling-interval') || '3') * 1000;
            const serverMaxTimeout = parseInt(statusDiv?.getAttribute('data-max-timeout') || '300') * 1000;
            
            if (serverPollingInterval) pollingInterval = serverPollingInterval;
            if (serverMaxTimeout) maxTimeout = serverMaxTimeout;
            
            // Store analysis start time for subsequent requests
            if (analysisStartTime && !options.analysisStartTime) {
                options.analysisStartTime = analysisStartTime;
            }
            
            // Update content - render inside a sandboxed iframe to isolate any
            // server-controlled scripts from the parent document (XSS hardening).
            renderContentInSandbox(content, data.Response);
            
            // Check completion
            if (status === 'COMPLETED' || status === 'FAILED') {
                btn.style.display = 'none';
                
                // Cache the final content for state preservation
                try {
                    const cacheKey = tabStateCache.generateKey(leadId, 'image_insights');
                    content.setAttribute('data-cache-key', cacheKey);
                    content.setAttribute('data-lead-id', leadId);
                    content.setAttribute('data-doc-type', 'image_insights');
                    
                    tabStateCache.store(cacheKey, data.Response, {
                        docType: 'image_insights',
                        timestamp: Date.now(),
                        leadId: leadId,
                        userId: options.userId
                    });
                } catch (cacheError) {
                    console.warn('[handleImageInsightsPolling] Failed to cache content:', cacheError);
                }
                
                console.log('Image insights %s for %s', status.toLowerCase(), leadId);
                return data.Response; // Return final result
            }
            
            // Check timeout
            if (Date.now() - startTime > maxTimeout) {
                content.innerHTML = `
                    <div style="
                        color: #dc2626; 
                        text-align: center; 
                        padding: 15px; 
                        background-color: #fee2e2; 
                        border: 1px solid #fecaca;
                        border-radius: 8px;
                        font-weight: 500;
                    ">
                        <i class="fas fa-clock" style="margin-right: 8px;"></i>
                        ⏰ Analysis timeout. Please try again later.
                    </div>
                `;
                btn.style.display = 'none';
                return;
            }
            
            // Continue polling
            setTimeout(poll, pollingInterval);
            
        } catch (error) {
            console.error('Error during %s polling:', docName, error);
            
            // Show error message in content area
            content.innerHTML = `
                <div style="
                    color: #dc2626; 
                    text-align: center; 
                    padding: 15px; 
                    background-color: #fee2e2; 
                    border: 1px solid #fecaca;
                    border-radius: 8px;
                    font-weight: 500;
                ">
                    <i class="fas fa-exclamation-triangle" style="margin-right: 8px;"></i>
                    ❌ Error during analysis. Please try again.
                    <small style="color: #991b1b; margin-top: 5px; display: block;">
                        ${error.message || 'Network error occurred'}
                    </small>
                </div>
            `;
            
            // Restore button for retry
            btn.innerHTML = '<i class="fas fa-redo"></i> Retry Analysis';
            btn.disabled = false;
            btn.style.cursor = 'pointer';
            btn.style.opacity = '1';
            btn.style.display = 'block';
            
            throw error;
        }
    };
    
    // Start polling with appropriate loading message
    // Static loading spinner. The @keyframes camCopilotSpin is defined in
    // src/index.css (moved out of this file to avoid template-literal false
    // positives). No user-controlled data is interpolated here.
    btn.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><div style="width: 16px; height: 16px; border: 2px solid #ffffff40; border-top: 2px solid #ffffff; border-radius: 50%; animation: camCopilotSpin 1s linear infinite;"></div><span>🔄 Analyzing Images...</span></div>';
    btn.disabled = true;
    btn.style.cursor = 'not-allowed';
    btn.style.opacity = '0.8';
    
    await poll();
};

// Function to initialize global functions
const initializeGlobalFunctions = () => {
    // Attach functions to window object to make them globally accessible
    window.processAsyncDocument = processAsyncDocument;
    
    // You can add more global functions here in the future
    // window.anotherGlobalFunction = anotherGlobalFunction;
};

// Export the initialization function
export default initializeGlobalFunctions;

// Also export individual functions if needed elsewhere in React
export { processAsyncDocument, renderFoliumMap };